import { i18n } from '@/shared/i18n';
import { deriveRates, type DeliveryTotals } from './metrics';

export interface FunnelStep {
  label: string;
  /** Null when the underlying total is unknown, not a measured zero. */
  value: number | null;
  rateFromPrevious: number | null;
}

/**
 * Impression -> click -> conversion, with the rate between each pair of steps rather than
 * each step's share of the first. Rates come from `deriveRates`, not from dividing here:
 * it already defines CVR as conversions over clicks (not impressions) and guards every
 * zero denominator, so reusing it is what keeps this funnel and the efficiency tiles from
 * ever disagreeing on what CTR or CVR means.
 *
 * Labels resolve through the `i18n` singleton at call time rather than from a table above:
 * a module-scope table would freeze them at import, and the caller is a component that
 * rebuilds the steps on every render.
 */
export function buildFunnel(totals: DeliveryTotals): FunnelStep[] {
  const { ctr, cvr } = deriveRates(totals);

  return [
    {
      label: i18n.t('projects:performanceDashboard.funnel.impressions'),
      value: totals.impressions,
      rateFromPrevious: null,
    },
    {
      label: i18n.t('projects:performanceDashboard.funnel.clicks'),
      value: totals.clicks,
      rateFromPrevious: ctr,
    },
    {
      label: i18n.t('projects:performanceDashboard.funnel.conversions'),
      value: totals.conversions,
      rateFromPrevious: cvr,
    },
  ];
}
